import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LuChevronDown, LuGlobe, LuLogOut, LuUser } from 'react-icons/lu';

import PATHS from '../../../../Routers/PATHS';
import checkLang from '../../../../helpers/checkLang';
import { signout } from '../../../../actions';
import avatarFallback from '../../../../res/images/avatar.svg';
import useDismissable from '../../hooks/useDismissable';

/**
 * The reader's name, avatar and the menu behind them.
 *
 * The trigger is avatar, name, chevron in DOM order, which the right-to-left
 * bar renders as chevron, name, avatar from the left — the design's own
 * arrangement — and mirrors in English without a second set of classes.
 *
 * Open/closed is the header's, not this component's: only one of the two
 * dropdowns may be down at a time, and a panel that owns its own boolean
 * cannot promise that. See `useDismissable`.
 *
 * Every row carries its icon at the inline start and its label after it,
 * aligned to the same edge. That is the design's own arrangement, and the same
 * one the profile aside uses for its four lines.
 *
 * THE THREE ROWS
 *
 * The profile link is a route this redesign adds — until now the only way to
 * edit a profile was a modal with no URL.
 *
 * "تغيير اللغة" goes through `checkLang`, never through `i18n.changeLanguage`.
 * That helper also swaps the global RTL/LTR stylesheets and stores the choice
 * in a cookie; changing the language by hand here would get the words right
 * and leave the page laid out the other way round.
 *
 * Signing out dispatches the existing thunk untouched. It clears the session
 * cookie, empties the store and sends the reader home, and reimplementing any
 * of that as a service here is exactly what the module rule forbids.
 */
const ROW =
    'tw-flex tw-w-full tw-items-center tw-gap-[12px] tw-rounded-[8px] tw-px-[14px] tw-py-[10px] ' +
    'tw-text-[14px] tw-leading-[1.5] tw-transition-colors ' +
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand';

const UserMenu = ({ user, isOpen, onToggle, onClose }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { triggerRef, panelRef } = useDismissable({ isOpen, onClose });

    const isArabic = i18n.language?.startsWith('ar');
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    const switchLanguage = () => {
        checkLang(isArabic ? 'en' : 'ar');
        onClose();
    };

    return (
        <div className="tw-relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                className={
                    'tw-flex tw-items-center tw-gap-[8px] tw-rounded-full tw-p-[2px] ' +
                    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                }
            >
                {/* Avatar, name, chevron — in that DOM order, because the bar
                    is right-to-left and the design puts the avatar at the
                    right-hand end of the three with the chevron furthest from
                    it. In English the row mirrors and the avatar leads on the
                    left, which is where a reader of that language looks. */}
                <img
                    src={user?.coverImgUrl || avatarFallback}
                    alt=""
                    aria-hidden
                    className="tw-size-[36px] tw-shrink-0 tw-rounded-full tw-object-cover"
                />

                <span className="tw-hidden sm:tw-inline tw-text-[14px] tw-font-bold tw-leading-[1.6] tw-text-v2-student-name tw-whitespace-nowrap">
                    {fullName}
                </span>

                <LuChevronDown
                    size={12}
                    aria-hidden
                    className={`tw-text-v2-student-name tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`}
                />
            </button>

            {/* The panel hangs from the trigger's far edge, so it opens back
                across the bar rather than off it: this cluster sits at the
                outer end of the header, and 220px anchored the other way would
                run past the viewport in Arabic. */}
            {isOpen && (
                <div
                    ref={panelRef}
                    role="menu"
                    aria-label={t('v2.student.menu.label')}
                    className={
                        'tw-absolute tw-top-[calc(100%+10px)] tw-z-50 tw-w-[220px] tw-bg-white ' +
                        'tw-rounded-[12px] tw-border tw-border-solid tw-border-v2-line-card ' +
                        'tw-shadow-v2-card tw-p-[6px]'
                    }
                    style={{ insetInlineEnd: 0 }}
                >
                    <Link
                        to={PATHS.USER_PROFILE.URL}
                        role="menuitem"
                        onClick={onClose}
                        className={`${ROW} tw-text-v2-student-name hover:tw-bg-v2-neutral-50`}
                    >
                        <LuUser size={16} aria-hidden className="tw-shrink-0" />
                        <span className="tw-flex-1 tw-text-start">{t('v2.student.menu.profile')}</span>
                    </Link>

                    <button
                        type="button"
                        role="menuitem"
                        onClick={switchLanguage}
                        className={`${ROW} tw-text-v2-student-name hover:tw-bg-v2-neutral-50`}
                    >
                        <LuGlobe size={16} aria-hidden className="tw-shrink-0" />
                        <span className="tw-flex-1 tw-text-start">{t('v2.student.menu.language')}</span>
                    </button>

                    <hr className="tw-my-[6px] tw-border-0 tw-border-t tw-border-solid tw-border-v2-line-card" />

                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => { onClose(); dispatch(signout()); }}
                        className={`${ROW} tw-text-v2-student-danger hover:tw-bg-v2-student-danger-tint`}
                    >
                        <LuLogOut size={16} aria-hidden className="tw-shrink-0" />
                        <span className="tw-flex-1 tw-text-start">{t('v2.student.menu.signOut')}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
